import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { LoadFailure } from '../../components/LoadFailure';
import { CardHeader, CardHeading, CardSubline, Footnote, GroupLabel, Loading, RowsFrame, SaveFailed, SettingsCard } from './cardParts';
import type { PlanListItem, PlanLists, PlanSettings, PlanSignOffRule } from './settingsApi';
import { SETTINGS } from './tokens';

export const PLAN_RULE_SAVED_TOAST = 'Sign-off rule saved. New sign-off requests use it now.';

const GROUPS: { key: keyof PlanLists; label: string }[] = [
  { key: 'classroom', label: 'Classroom' },
  { key: 'contentTests', label: 'Content tests' },
  { key: 'access', label: 'ACCESS' },
];

const RULES: { value: PlanSignOffRule; label: string; hint: string }[] = [
  { value: 'el_teacher_only', label: 'EL teacher only', hint: 'The EL teacher is asked to sign off each finalized plan.' },
  { value: 'every_teacher', label: 'Every teacher of record', hint: 'Every teacher Flashlight360 lists for the student is asked, the EL teacher included.' },
];

function itemNote(item: PlanListItem, lists: PlanLists): string {
  if (item.requiresIepOr504) return 'Only with an IEP or 504 plan';
  if (item.matchesClassroomItem === 'none') return 'No classroom counterpart';
  if (item.matchesClassroomItem) {
    const match = lists.classroom.find((c) => c.id === item.matchesClassroomItem);
    return match ? `Matches ${match.label} in class` : '';
  }
  return '';
}

type Props = {
  source: () => Promise<PlanSettings>;
  save: (rule: PlanSignOffRule) => Promise<PlanSettings>;
  onToast: (message: string) => void;
};

export function PlansTab({ source, save, onToast }: Props) {
  const [settings, setSettings] = useState<PlanSettings | null>(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    let active = true;
    source()
      .then((s) => {
        if (active) setSettings(s);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [source]);

  const pick = async (rule: PlanSignOffRule) => {
    if (!settings || saving || rule === settings.signOffRule) return;
    setSaving(true);
    setSaveFailed(false);
    try {
      const saved = await save(rule);
      setSettings(saved);
      onToast(PLAN_RULE_SAVED_TOAST);
    } catch {
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  };

  if (failed) return <LoadFailure tokens={SETTINGS} />;
  if (!settings) return <Loading />;

  return (
    <>
      <SettingsCard labelledBy="plan-lists-heading">
        <CardHeader>
          <Box>
            <CardHeading id="plan-lists-heading">Accommodation lists</CardHeading>
            <CardSubline>The lists staff pick from on each student&rsquo;s plan: classroom, content tests and ACCESS. Only items on these lists can go on a plan.</CardSubline>
          </Box>
        </CardHeader>
        <RowsFrame>
          {GROUPS.map((group) => (
            <Box key={group.key} data-testid={`plan-list-${group.key}`}>
              <GroupLabel>{group.label}</GroupLabel>
              {settings.lists[group.key].map((item) => {
                const note = itemNote(item, settings.lists);
                return (
                  <Box key={item.id} sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '16px', p: '8px 16px', borderTop: `1px solid ${SETTINGS.divider}` }}>
                    <Typography sx={{ fontFamily: SETTINGS.font, fontSize: 16, lineHeight: '24px', color: SETTINGS.ink }}>{item.label}</Typography>
                    {note && <Typography sx={{ fontFamily: SETTINGS.font, fontSize: 13, lineHeight: '20px', color: SETTINGS.muted, whiteSpace: 'nowrap' }}>{note}</Typography>}
                  </Box>
                );
              })}
            </Box>
          ))}
        </RowsFrame>
        <Footnote>Ask Flashlight to change these lists.</Footnote>
      </SettingsCard>

      <SettingsCard labelledBy="plan-signoff-heading">
        <CardHeader>
          <Box>
            <CardHeading id="plan-signoff-heading">Teacher sign-off</CardHeading>
            <CardSubline>Who is asked to sign off once a plan is finalized.</CardSubline>
          </Box>
        </CardHeader>
        <Box role="radiogroup" aria-labelledby="plan-signoff-heading" sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {RULES.map((rule) => (
            <Box key={rule.value} component="label" sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: saving ? 'default' : 'pointer' }}>
              <Box
                component="input"
                type="radio"
                name="plan-sign-off-rule"
                value={rule.value}
                checked={settings.signOffRule === rule.value}
                disabled={saving}
                onChange={() => {
                  void pick(rule.value);
                }}
                sx={{ mt: '4px', accentColor: SETTINGS.primary }}
              />
              <Box>
                <Typography sx={{ fontFamily: SETTINGS.font, fontSize: 16, lineHeight: '24px', fontWeight: 700, color: SETTINGS.ink }}>{rule.label}</Typography>
                <Typography sx={{ fontFamily: SETTINGS.font, fontSize: 14, lineHeight: '20px', color: SETTINGS.muted }}>{rule.hint}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
        <SaveFailed visible={saveFailed} />
      </SettingsCard>
    </>
  );
}
